/**
 * The pieces every screen is built out of.
 *
 * All of them take `className` rather than style props, because the CSP forbids
 * inline styles outright — see the note at the top of styles.css. Anything that
 * looks like it wants a colour prop gets a modifier class instead.
 */

import type { ReactNode } from 'react';

export const fmt = (n: number) => n.toLocaleString('en-US');

export function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

export function Page({ children }: { children: ReactNode }) {
  return (
    <div className="page">
      <div className="shell">{children}</div>
    </div>
  );
}

/** Six pips, because it is a six-dice game. */
export function Pips() {
  return (
    <div className="pips" aria-hidden="true">
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <span key={i} />
      ))}
    </div>
  );
}

export function Eyebrow({
  children,
  tone,
  className,
}: {
  children: ReactNode;
  tone?: 'dim' | 'ink' | 'ivory' | 'blood';
  className?: string;
}) {
  return <div className={cx('eyebrow', tone && `eyebrow--${tone}`, className)}>{children}</div>;
}

export function Masthead({ sub }: { sub: string }) {
  return (
    <div className="masthead">
      <h1>FARKLE</h1>
      <Pips />
      <Eyebrow>{sub}</Eyebrow>
    </div>
  );
}

/** A segmented choice chip. `wide` makes a row of them share the width evenly. */
export function Opt({
  on,
  onClick,
  wide,
  children,
  label,
}: {
  on: boolean;
  onClick: () => void;
  wide?: boolean;
  children: ReactNode;
  label?: string;
}) {
  return (
    <button
      type="button"
      className={cx('opt', on && 'opt--on', wide && 'opt--wide')}
      onClick={onClick}
      aria-pressed={on}
      aria-label={label}
    >
      {children}
    </button>
  );
}

export function Check({
  on,
  onClick,
  children,
}: {
  on: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button type="button" className={cx('check', on && 'check--on')} onClick={onClick} aria-pressed={on}>
      <span className="box" aria-hidden="true">
        {on ? '✓' : ''}
      </span>
      <span>{children}</span>
    </button>
  );
}

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <Eyebrow className="field-label">{label}</Eyebrow>
      {children}
    </>
  );
}
